import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Modal } from "components/Modal/Modal";
import { closeModal } from "store/modals/slice";

import { useTranslation } from "react-i18next";
import { getThreadById } from "services/support/threadsService";
import { ThreadResponse } from "pages/Support/Threads/mockThreads";
import "./Thread.scss";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import Button from "components/buttons/Button";

type InternalNote = {
  id: number;
  author: string;
  text: string;
  createdAt: string;
};

const ThreadModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");
  const isEditMode = Boolean(entityId);

  const [thread, setThread] = useState<ThreadResponse>({
    meta: {
      id: Date.now() % 10000,
      userEmail: "",
      sourse: "chat",
      lang: "EN-GB",
      type: "other",
      title: "",
      createdAt: "",
      updatedAt: "",
      assignee: "",
      status: "open",
    },
    details: {
      id: Date.now() % 10000,
      user: {
        id: Date.now(),
        email: "",
        language: "EN-GB",
        registeredAt: "",
        vipLevel: "silver",
      },
      messages: [],
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [notes, setNotes] = useState<InternalNote[]>([]);
  const [noteText, setNoteText] = useState("");
  const [replyDraft, setReplyDraft] = useState("");
  const [replyPreview, setReplyPreview] = useState("");
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!replyDraft.trim()) {
      setReplyPreview("");
      return;
    }

    const timeout = setTimeout(() => {
      setReplyPreview(replyDraft);
    }, 300);

    return () => clearTimeout(timeout);
  }, [replyDraft]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread.details.messages.length]);

  const handleAddNote = () => {
    if (!noteText.trim()) return;

    setNotes((prev) => [
      ...prev,
      {
        id: Date.now(),
        author: "Sarah K.",
        text: noteText,
        createdAt: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);

    setNoteText("");
  };

  const handleCloseClick = () => {
    navigate(location.pathname);
    dispatch(closeModal());
  };

  const fetchThreadById = async (id: number) => {
    setIsLoading(true);
    try {
      const result = await getThreadById(id);
      if (!result) return;
      setThread(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isEditMode || !entityId) return;
    fetchThreadById(Number(entityId));
  }, [isEditMode, entityId]);

  const handleSendReply = () => {
    if (!replyDraft.trim()) return;

    const newMessage = {
      id: Date.now(),
      author: "agent" as const,
      agentName: "Sarah K.",
      text: replyDraft,
      createdAt: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    setThread((prev) => ({
      ...prev,
      details: {
        ...prev.details,
        messages: [...prev.details.messages, newMessage],
      },
      meta: { ...prev.meta, updatedAt: new Date().toLocaleString() },
    }));

    setReplyDraft("");
    setReplyPreview("");
  };

  const getFirstLetter = (email: string) =>
    email.trim().charAt(0).toUpperCase();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;

      const activeElement = document.activeElement as HTMLElement | null;
      if (!activeElement) return;

      if (activeElement.id === "reply" && replyDraft.trim()) {
        e.preventDefault();
        handleSendReply();
      }

      if (activeElement.id === "internal-note" && noteText.trim()) {
        e.preventDefault();
        handleAddNote();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [replyDraft, noteText]);

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header />
      <Modal.Content>
        <div className="modal">
          <div className="modal__header">
            <div className="modal__header--tread-info">
              <div className="modal__header--tread-info--items">
                <span className="thread-id">
                  {t("threadId")} #{thread.details.id}
                </span>
                <div className={`sourse tag ${thread.meta.sourse}`}>
                  {thread.meta.sourse}
                </div>
                <div className="tag">{thread.meta.lang}</div>
                <div className={`tag ${thread.meta.type}`}>
                  {thread.meta.type}
                </div>
              </div>
              <div className="modal__header--tread-info--items">
                <div className="info--item">
                  {t("created")}: {thread.meta.createdAt}
                </div>
                <div className="info--item">
                  {t("updated")}: {thread.meta.updatedAt}
                </div>
              </div>
            </div>
            <div className="modal__header--selects">
              <DefaultDropdown
                value={thread.meta.status}
                options={[
                  { value: "open", label: t("open") },
                  { value: "pending", label: t("pending") },
                  { value: "resolved", label: t("resolved") },
                ]}
                onSelect={(selected) =>
                  setThread((prev) => ({
                    ...prev,
                    meta: { ...prev.meta, status: selected as any },
                  }))
                }
                disabled={thread.meta.status === "resolved"}
              />
              <DefaultDropdown
                placeholder={t("assignee")}
                value={thread.meta.assignee}
                options={[
                  { value: "Sarah K.", label: "Sarah K." },
                  { value: "Luca B.", label: "Luca B." },
                  { value: "Emily R.", label: "Emily R." },
                  { value: "Marco D.", label: "Marco D." },
                  { value: "Daniel P.", label: "Daniel P." },
                  { value: "Anna S.", label: "Anna S." },
                ]}
                onSelect={(selected) =>
                  setThread((prev) => ({
                    ...prev,
                    meta: { ...prev.meta, assignee: selected as string },
                  }))
                }
                disabled={isEditMode}
              />
            </div>
          </div>

          <div className="modal__body">
            <div className="modal__threads">
              <div className="modal__content">
                <div className="thread-messages">
                  {thread.details.messages.map((message) => (
                    <div
                      key={message.id}
                      className={`thread-message-wrapper thread-message--${message.author}`}
                    >
                      <div className="thread-message__meta">
                        <div className="thread-message__avatar">
                          {message.author === "agent"
                            ? message.agentName?.charAt(0).toUpperCase()
                            : getFirstLetter(thread.meta.userEmail)}
                        </div>
                        <span className="thread-message__author">
                          {message.author === "agent"
                            ? message.agentName
                            : thread.meta.userEmail}
                        </span>
                        {message.author === "agent" && (
                          <div className="tag thread-message__badge">
                            {t("agent")}
                          </div>
                        )}
                        <span className="thread-message__time">
                          {message.createdAt}
                        </span>
                      </div>

                      <div className="thread-message__message">
                        {message.author === "agent" && (
                          <div className="thread-message__block translated">
                            <div className="thread-info-wrapper">
                              <div className="thread-message__label">
                                {t("translated")}
                              </div>
                              <div className="thread-message__lang">
                                (EN-en)
                              </div>
                            </div>
                            <div className="thread-message__translation">
                              {message.text}
                            </div>
                          </div>
                        )}
                        {message.author === "agent" && (
                          <div className="separator"></div>
                        )}
                        <div className="thread-message__block original">
                          <div className="thread-info-wrapper">
                            <div className="thread-message__label">
                              {t("origin")}
                            </div>
                            <div className="thread-message__lang">
                              ({thread.details.user.language})
                            </div>
                          </div>
                          <div className="thread-message__original">
                            {message.text}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div className="modal__options">
                <div>
                  <span className="block-title">{t("reply")}</span>
                  <InnerInput
                    id="reply"
                    type="textarea"
                    value={replyDraft}
                    placeholder={t("replyPlaceholder")}
                    onChange={setReplyDraft}
                    disabled={thread.meta.status === "resolved"}
                  />
                </div>
                <div>
                  <span className="block-title">{t("preview")}</span>
                  <div className="fake-translation">
                    {replyPreview || t("previewPlaceholder")}
                  </div>
                </div>
                <Button
                  txt={t("sendReply")}
                  isSmall
                  isSecondary
                  disabled={thread.meta.status === "resolved"}
                  handleClick={handleSendReply}
                />
              </div>
            </div>

            <div className="modal__user">
              <div className="user-info-wrapper">
                <span className="block-title">{t("userInfo")}</span>
                <div className="block-info">
                  <div className="info-item-wrapper">
                    <span className="info-item">{t("email")}</span>
                    <div className="info-description">
                      {thread.details.user.email}
                    </div>
                  </div>
                  <div className="info-item-wrapper">
                    <span className="info-item">{t("userId")}</span>
                    <div className="info-description">
                      {thread.details.user.id}
                    </div>
                  </div>
                  <div className="info-item-wrapper">
                    <span className="info-item">{t("language")}</span>
                    <div className="info-description">
                      {thread.details.user.language}
                    </div>
                  </div>
                  <div className="info-item-wrapper">
                    <span className="info-item">{t("registered")}</span>
                    <div className="info-description">
                      {thread.details.user.registeredAt}
                    </div>
                  </div>
                  <div className="info-item-wrapper">
                    <span className="info-item">{t("vipLevel")}</span>
                    <div className={`tag ${thread.details.user.vipLevel}`}>
                      {thread.details.user.vipLevel}
                    </div>
                  </div>
                </div>
              </div>
              <div className="user-notes">
                <span className="block-title">{t("internalNotes")}</span>
                <div className="internal-notes">
                  <div className="add-notes-wrapper">
                    {notes.map((note) => (
                      <div key={note.id} className="internal-note">
                        <div className="internal-note__meta">
                          <span>{note.author}</span>
                          <span>{note.createdAt}</span>
                        </div>
                        <div className="internal-note__text">{note.text}</div>
                      </div>
                    ))}
                  </div>
                  <InnerInput
                    id="internal-note"
                    type="textarea"
                    value={noteText}
                    placeholder={t("addNotePlaceholder")}
                    onChange={setNoteText}
                  />
                  <Button
                    txt={t("addNote")}
                    isSmall
                    isSecondary
                    disabled={!noteText.trim()}
                    handleClick={handleAddNote}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default ThreadModal;
